import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useBilling } from "../contexts/BillingContext";
import { apiRequest, ApiError } from "../api/client";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { PlanLimitModal } from "../components/billing/PlanLimitModal";
import { FloatingToast } from "../components/common/FloatingToast";
import { IconLock, IconPlus, IconSearch, IconTrash2, IconUnlock, IconX, IconZap } from "../components/common/WireframeIcons";
import { PatternFieldHelp } from "../components/common/PatternFieldHelp";
import { MONTH_NAMES } from "../constants/dates";
import { formatCurrency } from "../utils/currency";
import { Account, AccountType, Category, CategoryKind, CategoryRule, CategoryRuleApi, CreditCard } from "../types/dashboard";
import { PlanLimitDetails } from "../types/billing";
import { getPlanLimitDetails, getPlanLimitLabel } from "../utils/billing";
type MaybeAll<T extends number> = T | "all";

const CATEGORY_COLOR_OPTIONS: Record<CategoryKind, string[]> = {
  expense: ["#2563eb", "#0ea5e9", "#f59e0b", "#f97316", "#ef4444", "#ec4899", "#8b5cf6", "#6366f1"],
  income: ["#15803d", "#16a34a", "#22c55e", "#4ade80", "#86efac"],
  transfer: ["#475569", "#64748b", "#94a3b8", "#cbd5f5", "#e2e8f0"],
};

const getDefaultCategoryColor = (kind: CategoryKind) => CATEGORY_COLOR_OPTIONS[kind][0];
const normalizeColorForKind = (kind: CategoryKind, color: string | null) => {
  if (color === null) return null;
  const palette = CATEGORY_COLOR_OPTIONS[kind];
  return palette.includes(color) ? color : palette[0];
};

interface Transaction {
  id: number;
  description: string | null;
  amount: number;
  transaction_date: string;
  value_date: string | null;
  is_credit: boolean;
  category_locked: boolean;
  metadata: Record<string, unknown>;
  account: {
    id: number;
    name: string;
    type: AccountType;
    currency: string;
  };
  category: {
    id: number;
    name: string;
    kind: CategoryKind;
    color: string | null;
  } | null;
}

interface ReassignCategoriesResponse {
  mode: "uncategorized" | "all";
  processed: number;
  matched: number;
  updated: number;
  unchanged: number;
  skippedWithoutDescription: number;
  rulesApplied: number;
}

interface Filters {
  year: number;
  month: MaybeAll<number>;
  accountId: MaybeAll<number>;
  categoryId: MaybeAll<number>;
}

interface CardFilters {
  year: number;
  month: MaybeAll<number>;
  cardId: MaybeAll<number>;
  categoryId: MaybeAll<number>;
}

interface CardTransaction {
  id: number;
  description: string | null;
  amount: number;
  transaction_date: string;
  value_date: string | null;
  card: {
    id: number;
    name: string;
    currency: string;
  };
  category: {
    id: number;
    name: string;
    kind: "income" | "expense" | "transfer";
    color: string | null;
  } | null;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short" }).format(new Date(value));
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function parseRuleAmount(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    throw new Error("El importe introducido no es válido");
  }
  return parsed;
}

const TRANSACTIONS_PER_PAGE = 50;

export default function DashboardPage() {
  const { token, logout } = useAuth();
  const { startCheckout, refreshBilling } = useBilling();
  const currentYear = new Date().getFullYear();
  const currentMonth = (new Date().getMonth() + 1) as MaybeAll<number>;

  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [cards, setCards] = React.useState<CreditCard[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [cardTransactions, setCardTransactions] = React.useState<CardTransaction[]>([]);
  const [movementScope, setMovementScope] = React.useState<"accounts" | "cards">("accounts");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [cardCurrentPage, setCardCurrentPage] = React.useState(1);
  const [categoryRules, setCategoryRules] = React.useState<CategoryRule[]>([]);

  const [filters, setFilters] = React.useState<Filters>({
    year: currentYear,
    month: currentMonth,
    accountId: "all",
    categoryId: "all",
  });
  const [cardFilters, setCardFilters] = React.useState<CardFilters>({
    year: currentYear,
    month: currentMonth,
    cardId: "all",
    categoryId: "all",
  });

  const [loadingBase, setLoadingBase] = React.useState(true);
  const [loadingTransactions, setLoadingTransactions] = React.useState(true);
  const [loadingCardTransactions, setLoadingCardTransactions] = React.useState(true);
  const [globalMessage, setGlobalMessage] = React.useState<string | null>(null);
  const [globalError, setGlobalError] = React.useState<string | null>(null);

  const [forceCategoryDrafts, setForceCategoryDrafts] = React.useState<Record<number, string>>({});
  const [forcingCategoryId, setForcingCategoryId] = React.useState<number | null>(null);
  const [reassigningMode, setReassigningMode] = React.useState<"uncategorized" | "all" | null>(null);
  const [quickCategoryModal, setQuickCategoryModal] = React.useState<{
    scope: "accounts" | "cards";
    transactionId: number;
    suggestedKind: CategoryKind;
  } | null>(null);
  const [quickCategoryForm, setQuickCategoryForm] = React.useState({
    name: "",
    kind: "expense" as CategoryKind,
    color: getDefaultCategoryColor("expense") as string | null,
    initialRule: { pattern: "", minAmount: "", maxAmount: "" },
  });
  const [quickCategorySubmitting, setQuickCategorySubmitting] = React.useState(false);
  const [planLimitDetails, setPlanLimitDetails] = React.useState<PlanLimitDetails | null>(null);
  const [billingActionLoading, setBillingActionLoading] = React.useState<null | "month" | "year">(null);
  const quickCategoryHeadingId = React.useId();
  const quickCategorySubtitleId = React.useId();
  const categorySelectStyle = React.useCallback(
    (color: string | null | undefined): React.CSSProperties => ({
      borderLeft: `4px solid ${color ?? "#cbd5e1"}`,
      paddingLeft: "0.55rem",
    }),
    []
  );

  const notifySuccess = React.useCallback((message: string) => {
    setGlobalMessage(message);
    setGlobalError(null);
  }, []);

  const notifyError = React.useCallback((message: string) => {
    setGlobalError(message);
    setGlobalMessage(null);
  }, []);

  const handlePlanLimitError = React.useCallback(
    (error: unknown) => {
      const details = getPlanLimitDetails(error);
      if (!details) return false;
      setPlanLimitDetails(details);
      notifyError(`${getPlanLimitLabel(details.metric)}: ${details.current}/${details.limit}. Pásate a Pro para continuar.`);
      void refreshBilling();
      return true;
    },
    [notifyError, refreshBilling]
  );

  const handleUpgradeMonthly = React.useCallback(async () => {
    setBillingActionLoading("month");
    try {
      await startCheckout("month");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo abrir el checkout mensual"));
    } finally {
      setBillingActionLoading(null);
    }
  }, [startCheckout, notifyError]);

  const handleUpgradeYearly = React.useCallback(async () => {
    setBillingActionLoading("year");
    try {
      await startCheckout("year");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo abrir el checkout anual"));
    } finally {
      setBillingActionLoading(null);
    }
  }, [startCheckout, notifyError]);

  const adaptRule = React.useCallback((rule: CategoryRuleApi): CategoryRule => ({
    id: rule.id,
    categoryId: rule.category_id,
    pattern: rule.pattern,
    minAmount: rule.min_amount,
    maxAmount: rule.max_amount,
    createdAt: rule.created_at,
  }), []);

  const handleRequest = React.useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
        throw error;
      }
    },
    [logout]
  );

  const loadBaseData = React.useCallback(async () => {
    if (!token) return;
    setLoadingBase(true);
    setGlobalError(null);
    try {
      const [accountsData, cardsData, categoriesData, rulesData] = await Promise.all([
        handleRequest(apiRequest<Account[]>("/accounts", { token })),
        handleRequest(apiRequest<CreditCard[]>("/cards", { token })),
        handleRequest(apiRequest<Category[]>("/categories", { token })),
        handleRequest(apiRequest<CategoryRuleApi[]>("/categories/rules", { token })),
      ]);
      setAccounts(accountsData);
      setCards(cardsData);
      setCategories(categoriesData);
      setCategoryRules(rulesData.map(adaptRule));
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos cargar las cuentas ni las categorías"));
    } finally {
      setLoadingBase(false);
    }
  }, [token, handleRequest, notifyError, adaptRule]);

  const loadTransactions = React.useCallback(async () => {
    if (!token) return;
    setLoadingTransactions(true);
    const params = new URLSearchParams();
    params.set("year", String(filters.year));
    if (filters.month !== "all") params.set("month", String(filters.month));
    if (filters.accountId !== "all") params.set("accountId", String(filters.accountId));
    if (filters.categoryId !== "all") params.set("categoryId", String(filters.categoryId));

    const endpoint = `/transactions${params.toString() ? `?${params.toString()}` : ""}`;
    try {
      const rows = await handleRequest(apiRequest<Transaction[]>(endpoint, { token }));
      setTransactions(rows);
      setCurrentPage(1);
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos obtener los movimientos"));
    } finally {
      setLoadingTransactions(false);
    }
  }, [token, filters, handleRequest, notifyError]);

  const loadCardTransactions = React.useCallback(async () => {
    if (!token) return;
    setLoadingCardTransactions(true);
    const params = new URLSearchParams();
    params.set("year", String(cardFilters.year));
    if (cardFilters.month !== "all") params.set("month", String(cardFilters.month));
    if (cardFilters.cardId !== "all") params.set("cardId", String(cardFilters.cardId));
    if (cardFilters.categoryId !== "all") params.set("categoryId", String(cardFilters.categoryId));

    const endpoint = `/card-transactions${params.toString() ? `?${params.toString()}` : ""}`;
    try {
      const rows = await handleRequest(apiRequest<CardTransaction[]>(endpoint, { token }));
      setCardTransactions(rows);
      setCardCurrentPage(1);
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos obtener los movimientos de tarjeta"));
    } finally {
      setLoadingCardTransactions(false);
    }
  }, [token, cardFilters, handleRequest, notifyError]);

  React.useEffect(() => {
    if (!token) return;
    loadBaseData();
  }, [token, loadBaseData]);

  React.useEffect(() => {
    if (!token) return;
    loadTransactions();
  }, [token, loadTransactions]);

  React.useEffect(() => {
    if (!token) return;
    loadCardTransactions();
  }, [token, loadCardTransactions]);

  const years = React.useMemo(
    () => Array.from({ length: 6 }, (_, index) => currentYear - index),
    [currentYear]
  );

  const monthOptions = React.useMemo(
    () => [{ label: "Todo el año", value: "all" as MaybeAll<number> }].concat(
      MONTH_NAMES.map((label, index) => ({
        label,
        value: (index + 1) as MaybeAll<number>,
      }))
    ),
    []
  );

  const categoryOptions = React.useMemo(() => categories, [categories]);

  const getForceDraftValue = React.useCallback(
    (transactionId: number, currentCategoryId: number | null) => {
      const draft = forceCategoryDrafts[transactionId];
      if (draft !== undefined) return draft;
      return currentCategoryId ? String(currentCategoryId) : "";
    },
    [forceCategoryDrafts]
  );

  const totalPages = React.useMemo(() => Math.max(1, Math.ceil(transactions.length / TRANSACTIONS_PER_PAGE)), [transactions.length]);

  const paginatedTransactions = React.useMemo(() => {
    const start = (currentPage - 1) * TRANSACTIONS_PER_PAGE;
    return transactions.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [transactions, currentPage]);

  const paginationSummary = React.useMemo(() => {
    if (!transactions.length) return "0 movimientos";
    const start = (currentPage - 1) * TRANSACTIONS_PER_PAGE + 1;
    const end = Math.min(transactions.length, currentPage * TRANSACTIONS_PER_PAGE);
    return `${start}-${end} de ${transactions.length} movimientos`;
  }, [transactions.length, currentPage]);

  const cardTotalPages = React.useMemo(
    () => Math.max(1, Math.ceil(cardTransactions.length / TRANSACTIONS_PER_PAGE)),
    [cardTransactions.length]
  );

  const paginatedCardTransactions = React.useMemo(() => {
    const start = (cardCurrentPage - 1) * TRANSACTIONS_PER_PAGE;
    return cardTransactions.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [cardTransactions, cardCurrentPage]);

  const cardPaginationSummary = React.useMemo(() => {
    if (!cardTransactions.length) return "0 movimientos";
    const start = (cardCurrentPage - 1) * TRANSACTIONS_PER_PAGE + 1;
    const end = Math.min(cardTransactions.length, cardCurrentPage * TRANSACTIONS_PER_PAGE);
    return `${start}-${end} de ${cardTransactions.length} movimientos`;
  }, [cardTransactions.length, cardCurrentPage]);

  const handleToggleCategoryLock = async (transactionId: number) => {
    if (!token) return;
    const transaction = transactions.find(tx => tx.id === transactionId);
    if (!transaction) return;

    const isLocked = transaction.category_locked;
    const draftValue = getForceDraftValue(transactionId, transaction.category ? transaction.category.id : null);
    const parsedCategoryId = draftValue ? Number(draftValue) : null;

    if (!isLocked && (!parsedCategoryId || Number.isNaN(parsedCategoryId))) {
      notifyError("Selecciona una categoría para bloquear el movimiento");
      return;
    }

    setForcingCategoryId(transactionId);
    try {
      await handleRequest(
        apiRequest<Transaction>(`/transactions/${transactionId}/force-category`, {
          method: "POST",
          token,
          body: {
            categoryId: parsedCategoryId ?? transaction.category?.id ?? null,
            locked: !isLocked,
          },
        })
      );
      notifySuccess(!isLocked ? "Categoría fijada y bloqueada" : "Movimiento desbloqueado");
      if (isLocked) {
        setForceCategoryDrafts(prev => ({
          ...prev,
          [transaction.id]: transaction.category ? String(transaction.category.id) : "",
        }));
      }
      await loadTransactions();
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos actualizar el bloqueo de la categoría"));
    } finally {
      setForcingCategoryId(null);
    }
  };

  const handleTransactionDelete = async (transactionId: number) => {
    if (!token) return;
    const confirmed = window.confirm("¿Quieres eliminar este movimiento?");
    if (!confirmed) return;
    try {
      await handleRequest(
        apiRequest<void>(`/transactions/${transactionId}`, {
          method: "DELETE",
          token,
        })
      );
      notifySuccess("Movimiento eliminado");
      await loadTransactions();
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo eliminar el movimiento"));
    }
  };

  const handleCardCategoryChange = async (transactionId: number, categoryId: string) => {
    if (!token) return;
    try {
      await handleRequest(
        apiRequest<CardTransaction>(`/card-transactions/${transactionId}`, {
          method: "PUT",
          token,
          body: { categoryId: categoryId ? Number(categoryId) : null },
        })
      );
      notifySuccess("Categoría actualizada");
      await loadCardTransactions();
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo actualizar la categoría"));
    }
  };

  const handleAccountCategoryChange = async (transactionId: number, categoryId: string, silent = false) => {
    if (!token) return;
    const transaction = transactions.find(tx => tx.id === transactionId);
    if (!transaction) return;
    try {
      await handleRequest(
        apiRequest<Transaction>(`/transactions/${transactionId}/force-category`, {
          method: "POST",
          token,
          body: {
            categoryId: categoryId ? Number(categoryId) : null,
            locked: transaction.category_locked,
          },
        })
      );
      if (!silent) notifySuccess("Categoría actualizada");
      await loadTransactions();
    } catch (error) {
      if (!silent) notifyError(extractErrorMessage(error, "No se pudo actualizar la categoría"));
    }
  };

  const openQuickCategoryModal = (scope: "accounts" | "cards", transactionId: number, amount: number) => {
    const inferredKind: CategoryKind = amount >= 0 ? "income" : "expense";
    setQuickCategoryModal({ scope, transactionId, suggestedKind: inferredKind });
    setQuickCategoryForm({
      name: "",
      kind: inferredKind,
      color: getDefaultCategoryColor(inferredKind),
      initialRule: { pattern: "", minAmount: "", maxAmount: "" },
    });
  };

  const closeQuickCategoryModal = () => {
    setQuickCategoryModal(null);
    setQuickCategoryForm({
      name: "",
      kind: "expense",
      color: getDefaultCategoryColor("expense"),
      initialRule: { pattern: "", minAmount: "", maxAmount: "" },
    });
  };

  const quickCategoryColorOptions = React.useMemo(() => {
    const palette = CATEGORY_COLOR_OPTIONS[quickCategoryForm.kind];
    if (quickCategoryForm.color && !palette.includes(quickCategoryForm.color)) {
      return [...palette, quickCategoryForm.color];
    }
    return palette;
  }, [quickCategoryForm.kind, quickCategoryForm.color]);

  const handleQuickCategoryCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !quickCategoryModal) return;
    if (!quickCategoryForm.name.trim()) {
      notifyError("El nombre de la categoría es obligatorio");
      return;
    }

    let initialRulePayload: { pattern: string; minAmount: number | null; maxAmount: number | null } | null = null;
    const initialRulePattern = quickCategoryForm.initialRule.pattern.trim();
    if (initialRulePattern) {
      try {
        initialRulePayload = {
          pattern: initialRulePattern,
          minAmount: parseRuleAmount(quickCategoryForm.initialRule.minAmount),
          maxAmount: parseRuleAmount(quickCategoryForm.initialRule.maxAmount),
        };
      } catch (error) {
        notifyError(error instanceof Error ? error.message : "El importe de la regla inicial es inválido");
        return;
      }
    }

    setQuickCategorySubmitting(true);
    try {
      const created = await handleRequest(
        apiRequest<Category>("/categories", {
          method: "POST",
          token,
          body: {
            name: quickCategoryForm.name.trim(),
            kind: quickCategoryForm.kind,
            color: quickCategoryForm.color,
          },
        })
      );
      setCategories(prev =>
        [...prev, created].sort((a, b) =>
          a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind.localeCompare(b.kind)
        )
      );

      if (initialRulePayload) {
        await handleRequest(
          apiRequest<CategoryRuleApi>(`/categories/${created.id}/rules`, {
            method: "POST",
            token,
            body: initialRulePayload,
          })
        );
      }

      if (quickCategoryModal.scope === "accounts") {
        await handleAccountCategoryChange(quickCategoryModal.transactionId, String(created.id), true);
      } else {
        await handleCardCategoryChange(quickCategoryModal.transactionId, String(created.id));
      }

      notifySuccess(`Categoría "${created.name}" creada y asignada`);
      closeQuickCategoryModal();
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(extractErrorMessage(error, "No se pudo crear la categoría"));
    } finally {
      setQuickCategorySubmitting(false);
    }
  };

  const handleReassignCategories = React.useCallback(
    async (mode: "uncategorized" | "all") => {
      if (!token) return;
      if (!categoryRules.length) {
        notifyError("Define al menos una regla de categorización antes de aplicar esta acción.");
        return;
      }
      if (mode === "all") {
        const shouldProceed = window.confirm(
          "Aplicaremos las reglas a todos los movimientos y podríamos sobrescribir categorías existentes. ¿Quieres continuar?"
        );
        if (!shouldProceed) return;
      }
      setReassigningMode(mode);
      try {
        const result = await handleRequest(
          apiRequest<ReassignCategoriesResponse>("/transactions/reassign-categories", {
            method: "POST",
            token,
            body: { mode },
          })
        );
        let message = `Aplicamos ${result.rulesApplied} reglas sobre ${result.processed} movimientos`;
        message += mode === "uncategorized" ? " sin categoría." : ".";
        message += ` ${result.updated} movimientos actualizaron su categoría.`;
        if (result.skippedWithoutDescription) {
          message += ` ${result.skippedWithoutDescription} sin descripción se mantuvieron igual.`;
        } else if (!result.updated && !result.matched) {
          message += " No encontramos coincidencias nuevas.";
        }
        notifySuccess(message.trim());
        await loadTransactions();
      } catch (error) {
        notifyError(extractErrorMessage(error, "No pudimos reasignar las categorías"));
      } finally {
        setReassigningMode(null);
      }
    },
    [
      token,
      categoryRules.length,
      handleRequest,
      notifyError,
      notifySuccess,
      loadTransactions,
    ]
  );

  const hasCategoryRules = categoryRules.length > 0;
  const disableReassignButtons = reassigningMode !== null || !hasCategoryRules;
  const isCardScope = movementScope === "cards";

  const accountKpis = React.useMemo(() => {
    const income = transactions.filter(tx => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
    const expenses = transactions.filter(tx => tx.amount < 0).reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    return { income, expenses, balance: income - expenses };
  }, [transactions]);

  const cardKpis = React.useMemo(() => {
    const income = cardTransactions.filter(tx => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
    const expenses = cardTransactions.filter(tx => tx.amount < 0).reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    return { income, expenses, balance: income - expenses };
  }, [cardTransactions]);

  const kpis = isCardScope ? cardKpis : accountKpis;

  return (
    <DashboardLayout active="operations">
      <div className="operations-page-header">
        <div className="operations-page-header-inner">
          <div className="operations-page-heading">
            <h1>Categorizar movimientos</h1>
            <p>
              {isCardScope
                ? "Revisa y categoriza los movimientos de tus tarjetas"
                : "Asigna categorías a tus transacciones bancarias y revisa el balance del periodo"}
            </p>
          </div>
          <div className="settings-subnav" role="tablist" aria-label="Tipo de movimientos">
            <button
              type="button"
              className={`settings-subnav-button${!isCardScope ? " is-active" : ""}`}
              role="tab"
              aria-selected={!isCardScope}
              onClick={() => setMovementScope("accounts")}
            >
              Cuentas bancarias ({transactions.length})
            </button>
            <button
              type="button"
              className={`settings-subnav-button${isCardScope ? " is-active" : ""}`}
              role="tab"
              aria-selected={isCardScope}
              onClick={() => setMovementScope("cards")}
            >
              Tarjetas ({cardTransactions.length})
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-page-content is-fullwidth grid operations-page-body">
          <FloatingToast
            message={globalError || globalMessage}
            tone={globalError ? "error" : "success"}
            onClear={() => {
              setGlobalError(null);
              setGlobalMessage(null);
            }}
          />

          {loadingBase ? (
            <div className="panel">
              <div className="loader">
                <div className="spinner" />
                Cargando tus datos...
              </div>
            </div>
          ) : (
            <>
              <section className="operations-filters-panel">
                <div className="panel-filters transactions-filter-grid">
                  <select
                    className="input"
                    aria-label="Filtrar por año"
                    value={isCardScope ? cardFilters.year : filters.year}
                    onChange={event =>
                      isCardScope
                        ? setCardFilters(prev => ({
                            ...prev,
                            year: Number(event.target.value),
                          }))
                        : setFilters(prev => ({
                            ...prev,
                            year: Number(event.target.value),
                          }))
                    }
                  >
                    {years.map(year => (
                      <option key={year} value={year}>
                        Año: {year}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input"
                    aria-label="Filtrar por mes"
                    value={isCardScope ? cardFilters.month : filters.month}
                    onChange={event =>
                      isCardScope
                        ? setCardFilters(prev => ({
                            ...prev,
                            month: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                          }))
                        : setFilters(prev => ({
                            ...prev,
                            month: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                          }))
                    }
                  >
                    {monthOptions.map(option => (
                      <option key={option.label} value={option.value}>
                        {option.value === "all" ? "Mes: Todos" : `Mes: ${option.label}`}
                      </option>
                    ))}
                  </select>
                  {!isCardScope ? (
                    <select
                      className="input"
                      aria-label="Filtrar por cuenta"
                      value={filters.accountId}
                      onChange={event =>
                        setFilters(prev => ({
                          ...prev,
                          accountId: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                        }))
                      }
                    >
                      <option value="all">Cuenta: Todas</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          Cuenta: {account.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      className="input"
                      aria-label="Filtrar por tarjeta"
                      value={cardFilters.cardId}
                      onChange={event =>
                        setCardFilters(prev => ({
                          ...prev,
                          cardId: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                        }))
                      }
                    >
                      <option value="all">Tarjeta: Todas</option>
                      {cards.map(card => (
                        <option key={card.id} value={card.id}>
                          Tarjeta: {card.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    className="input"
                    aria-label="Filtrar por categoría"
                    value={isCardScope ? cardFilters.categoryId : filters.categoryId}
                    onChange={event =>
                      isCardScope
                        ? setCardFilters(prev => ({
                            ...prev,
                            categoryId: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                          }))
                        : setFilters(prev => ({
                            ...prev,
                            categoryId: event.target.value === "all" ? "all" : Number(event.target.value) as MaybeAll<number>,
                          }))
                    }
                  >
                    <option value="all">Categoría: Todas</option>
                    {categoryOptions.map(category => (
                      <option key={category.id} value={category.id}>
                        Categoría: {category.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: "flex", justifyContent: "flex-end", alignSelf: "stretch" }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => (isCardScope ? loadCardTransactions() : loadTransactions())}
                      aria-label="Filtrar"
                      title="Filtrar"
                    >
                      <IconSearch className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>

              <section className="operations-overview-panel">
                <div className="cards-grid" style={{ marginTop: "0.25rem", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                  <article className="card">
                    <strong>Ingresos</strong>
                    <span className="stat-value positive">{formatCurrency(kpis.income)}</span>
                  </article>
                  <article className="card">
                    <strong>Gastos</strong>
                    <span className="stat-value negative">{formatCurrency(-Math.abs(kpis.expenses))}</span>
                  </article>
                  <article className="card">
                    <strong>Balance</strong>
                    <span className={`stat-value ${kpis.balance >= 0 ? "positive" : "negative"}`}>
                      {formatCurrency(kpis.balance)}
                    </span>
                  </article>
                </div>
              </section>

              <section className="panel operations-table-panel">
                <div className="panel-header">
                  <div className="panel-actions">
                    <span className="chip">
                      {isCardScope
                        ? loadingCardTransactions
                          ? "Cargando..."
                          : cardPaginationSummary
                        : loadingTransactions
                          ? "Cargando..."
                          : paginationSummary}
                    </span>
                    {!isCardScope && (
                      <div className="operations-rule-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: "0.4rem 0.8rem", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                          onClick={() => handleReassignCategories("uncategorized")}
                          disabled={disableReassignButtons}
                          title={
                            hasCategoryRules
                              ? undefined
                              : "Define reglas automáticas antes de usar esta acción"
                          }
                        >
                          <IconZap className="w-4 h-4" />
                          {reassigningMode === "uncategorized"
                            ? "Aplicando..."
                            : "Aplicar reglas a movimientos sin categoría"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ padding: "0.4rem 0.8rem", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                          onClick={() => handleReassignCategories("all")}
                          disabled={disableReassignButtons}
                          title={
                            hasCategoryRules
                              ? "Reaplica las reglas sobre todos los movimientos"
                              : "Define reglas automáticas antes de usar esta acción"
                          }
                        >
                          <IconZap className="w-4 h-4" />
                          {reassigningMode === "all" ? "Aplicando..." : "Aplicar reglas sobre todos los movimientos"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <hr className="panel-divider" />
                {isCardScope ? (
                  loadingCardTransactions ? (
                    <div className="loader">
                      <div className="spinner" />
                      Consultando movimientos...
                    </div>
                  ) : cardTransactions.length ? (
                    <div>
                      <div className="operations-table-desktop" style={{ overflowX: "auto" }}>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Descripción</th>
                              <th>Tarjeta</th>
                              <th>Categoría</th>
                              <th style={{ textAlign: "right" }}>Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedCardTransactions.map(transaction => (
                              <tr key={transaction.id}>
                                <td>{formatDate(transaction.transaction_date)}</td>
                                <td>{transaction.description || "—"}</td>
                                <td>{transaction.card.name}</td>
                                <td>
                                  <div className="operations-category-inline">
                                    <select
                                      className="input"
                                      value={transaction.category?.id ?? ""}
                                      style={categorySelectStyle(transaction.category?.color)}
                                      onChange={event => handleCardCategoryChange(transaction.id, event.target.value)}
                                    >
                                      <option value="">Sin categoría</option>
                                      {categoryOptions.map(category => (
                                        <option key={category.id} value={category.id}>
                                          {category.name}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      className="btn btn-secondary operations-category-inline-add"
                                      title="Crear categoría y asignar"
                                      onClick={() => openQuickCategoryModal("cards", transaction.id, transaction.amount)}
                                    >
                                      <IconPlus className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  <span className={`stat-value ${transaction.amount >= 0 ? "positive" : "negative"}`}>
                                    {formatCurrency(transaction.amount, transaction.card.currency)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="operations-table-mobile">
                        {paginatedCardTransactions.map(transaction => (
                          <article key={`mobile-card-${transaction.id}`} className="setup-entity-card">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                              <div>
                                <p style={{ margin: 0, fontWeight: 600, color: "#0f172a" }}>{transaction.description || "—"}</p>
                                <p style={{ margin: "0.2rem 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                                  {formatDate(transaction.transaction_date)} · {transaction.card.name}
                                </p>
                              </div>
                              <span className={`stat-value ${transaction.amount >= 0 ? "positive" : "negative"}`} style={{ fontSize: "0.95rem" }}>
                                {formatCurrency(transaction.amount, transaction.card.currency)}
                              </span>
                            </div>
                            <select
                              className="input"
                              value={transaction.category?.id ?? ""}
                              style={categorySelectStyle(transaction.category?.color)}
                              onChange={event => handleCardCategoryChange(transaction.id, event.target.value)}
                            >
                              <option value="">Sin categoría</option>
                              {categoryOptions.map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ minWidth: "48px" }}
                              title="Crear categoría y asignar"
                              onClick={() => openQuickCategoryModal("cards", transaction.id, transaction.amount)}
                            >
                              <IconPlus className="w-4 h-4" />
                            </button>
                          </article>
                        ))}
                      </div>
                      {cardTotalPages > 1 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "1rem",
                            flexWrap: "wrap",
                            gap: "0.75rem",
                          }}
                        >
                          <span style={{ color: "#334155", fontWeight: 600 }}>
                            Página {cardCurrentPage} de {cardTotalPages}
                          </span>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setCardCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={cardCurrentPage === 1}
                            >
                              Anterior
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setCardCurrentPage(prev => Math.min(cardTotalPages, prev + 1))}
                              disabled={cardCurrentPage === cardTotalPages}
                            >
                              Siguiente
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="empty-state">
                      Aún no hay movimientos de tarjetas para los filtros seleccionados.
                    </div>
                  )
                ) : loadingTransactions ? (
                  <div className="loader">
                    <div className="spinner" />
                    Consultando movimientos...
                  </div>
                ) : transactions.length ? (
                  <div>
                    <div className="operations-table-desktop" style={{ overflowX: "auto" }}>
                      <table className="table">
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Descripción</th>
                              <th>Cuenta</th>
                              <th>Categoría asignada</th>
                              <th style={{ textAlign: "right" }}>Importe</th>
                            </tr>
                          </thead>
                        <tbody>
                          {paginatedTransactions.map(transaction => (
                            <tr key={transaction.id}>
                              <td>{formatDate(transaction.transaction_date)}</td>
                                <td>{transaction.description || "—"}</td>
                                <td>{transaction.account.name}</td>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: "220px" }}>
                                  <select
                                    className="input"
                                    value={getForceDraftValue(transaction.id, transaction.category ? transaction.category.id : null)}
                                    style={categorySelectStyle(transaction.category?.color)}
                                    disabled={transaction.category_locked || forcingCategoryId === transaction.id}
                                    onChange={event => void handleAccountCategoryChange(transaction.id, event.target.value)}
                                  >
                                    <option value="">Selecciona categoría</option>
                                    {categoryOptions.map(category => (
                                      <option key={category.id} value={category.id}>
                                        {category.name}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={
                                      transaction.category_locked
                                        ? { minWidth: "48px", borderColor: "#93c5fd", color: "#1d4ed8", background: "#eff6ff" }
                                        : { minWidth: "48px", borderColor: "#fde68a", color: "#a16207", background: "#fffbeb" }
                                    }
                                    onClick={() => handleToggleCategoryLock(transaction.id)}
                                    disabled={forcingCategoryId === transaction.id}
                                    title={transaction.category_locked ? "Desbloquear categoría" : "Bloquear categoría"}
                                  >
                                    {forcingCategoryId === transaction.id
                                      ? "..."
                                      : transaction.category_locked
                                        ? <IconLock className="w-4 h-4" />
                                        : <IconUnlock className="w-4 h-4" />}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ minWidth: "48px", borderColor: "#bfdbfe", color: "#1d4ed8", background: "#eff6ff" }}
                                    title="Crear categoría y asignar"
                                    disabled={forcingCategoryId === transaction.id}
                                    onClick={() => openQuickCategoryModal("accounts", transaction.id, transaction.amount)}
                                  >
                                    <IconPlus className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <span className={`stat-value ${transaction.amount >= 0 ? "positive" : "negative"}`}>
                                  {formatCurrency(transaction.amount, transaction.account.currency)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="operations-table-mobile">
                      {paginatedTransactions.map(transaction => (
                        <article key={`mobile-${transaction.id}`} className="setup-entity-card">
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                            <div>
                              <p style={{ margin: 0, fontWeight: 600, color: "#0f172a" }}>{transaction.description || "—"}</p>
                              <p style={{ margin: "0.2rem 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                                {formatDate(transaction.transaction_date)} · {transaction.account.name}
                              </p>
                            </div>
                            <span className={`stat-value ${transaction.amount >= 0 ? "positive" : "negative"}`} style={{ fontSize: "0.95rem" }}>
                              {formatCurrency(transaction.amount, transaction.account.currency)}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <select
                              className="input"
                              value={getForceDraftValue(transaction.id, transaction.category ? transaction.category.id : null)}
                              style={categorySelectStyle(transaction.category?.color)}
                              disabled={transaction.category_locked || forcingCategoryId === transaction.id}
                              onChange={event => void handleAccountCategoryChange(transaction.id, event.target.value)}
                            >
                              <option value="">Selecciona categoría</option>
                              {categoryOptions.map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={
                                transaction.category_locked
                                  ? { minWidth: "48px", borderColor: "#93c5fd", color: "#1d4ed8", background: "#eff6ff" }
                                  : { minWidth: "48px", borderColor: "#fde68a", color: "#a16207", background: "#fffbeb" }
                              }
                              onClick={() => handleToggleCategoryLock(transaction.id)}
                              disabled={forcingCategoryId === transaction.id}
                              title={transaction.category_locked ? "Desbloquear categoría" : "Bloquear categoría"}
                            >
                              {forcingCategoryId === transaction.id
                                ? "..."
                                : transaction.category_locked
                                  ? <IconLock className="w-4 h-4" />
                                  : <IconUnlock className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ minWidth: "48px", borderColor: "#bfdbfe", color: "#1d4ed8", background: "#eff6ff" }}
                              title="Crear categoría y asignar"
                              disabled={forcingCategoryId === transaction.id}
                              onClick={() => openQuickCategoryModal("accounts", transaction.id, transaction.amount)}
                            >
                              <IconPlus className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ minWidth: "48px", borderColor: "#fecaca", color: "#b91c1c" }}
                              onClick={() => handleTransactionDelete(transaction.id)}
                              title="Eliminar"
                            >
                              <IconTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "1rem",
                          flexWrap: "wrap",
                          gap: "0.75rem",
                        }}
                      >
                        <span style={{ color: "#334155", fontWeight: 600 }}>
                          Página {currentPage} de {totalPages}
                        </span>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Anterior
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    Aún no hay movimientos para los filtros seleccionados.
                  </div>
                )}
              </section>

            </>
          )}
        </div>
        {quickCategoryModal && (
          <div className="settings-drawer-container settings-drawer-container--modal">
            <button
              type="button"
              className="settings-drawer-backdrop settings-drawer-backdrop--soft"
              aria-label="Cerrar"
              onClick={closeQuickCategoryModal}
            />
            <div
              className="settings-drawer settings-drawer--modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby={quickCategoryHeadingId}
              aria-describedby={quickCategorySubtitleId}
            >
              <button type="button" className="settings-drawer-close" onClick={closeQuickCategoryModal} aria-label="Cerrar">
                <IconX className="w-5 h-5" />
              </button>
              <form className="settings-drawer-form" onSubmit={handleQuickCategoryCreate}>
                <p className="settings-drawer-label">Nueva categoría</p>
                <h3 id={quickCategoryHeadingId} className="settings-drawer-title">Categoría rápida</h3>
                <p id={quickCategorySubtitleId} className="settings-drawer-subtitle">
                  Completa los campos para crear una nueva categoría e, opcionalmente, su primera regla automática.
                </p>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Nombre
                  <input
                    className="input"
                    value={quickCategoryForm.name}
                    onChange={event =>
                      setQuickCategoryForm(prev => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    placeholder="Supermercado"
                    required
                  />
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Tipo
                  <select
                    className="input"
                    value={quickCategoryForm.kind}
                    onChange={event => {
                      const nextKind = event.target.value as CategoryKind;
                      setQuickCategoryForm(prev => ({
                        ...prev,
                        kind: nextKind,
                        color: prev.color === null ? null : normalizeColorForKind(nextKind, prev.color),
                      }));
                    }}
                  >
                    <option value="expense">Gasto</option>
                    <option value="income">Ingreso</option>
                    <option value="transfer">Traspasos</option>
                  </select>
                </label>

                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Color
                  <div className="settings-drawer-color-grid">
                    {quickCategoryColorOptions.map(option => {
                      const isSelected = quickCategoryForm.color?.toLowerCase() === option.toLowerCase();
                      return (
                        <button
                          key={`quick-category-color-${option}`}
                          type="button"
                          onClick={() =>
                            setQuickCategoryForm(prev => ({
                              ...prev,
                              color: option,
                            }))
                          }
                          style={{
                            width: "1.85rem",
                            height: "1.85rem",
                            borderRadius: "999px",
                            border: isSelected ? "3px solid #0f172a" : "2px solid rgba(15, 23, 42, 0.15)",
                            background: option,
                            cursor: "pointer",
                            outline: "none",
                          }}
                          aria-label={`Seleccionar color ${option}`}
                        >
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() =>
                        setQuickCategoryForm(prev => ({
                          ...prev,
                          color: null,
                        }))
                      }
                      className="settings-drawer-outline-button"
                    >
                      Sin color
                    </button>
                  </div>
                </label>

                <div className="settings-drawer-section">
                  <p className="settings-drawer-label" style={{ letterSpacing: 0, fontSize: "0.78rem", color: "#334155" }}>
                    Regla inicial (opcional)
                  </p>
                  <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    <span className="field-label-with-help">
                      Texto en la descripción del movimiento
                      <PatternFieldHelp text="Intentará asignar a esta categoría todos los movimientos bancarios en cuya descripción aparezcan las palabras que indiques en el orden de aparición. Interpreta tanto mayúsculas como minúsculas." />
                    </span>
                    <input
                      className="input"
                      value={quickCategoryForm.initialRule.pattern}
                      onChange={event =>
                        setQuickCategoryForm(prev => ({
                          ...prev,
                          initialRule: { ...prev.initialRule, pattern: event.target.value },
                        }))
                      }
                      placeholder="Ej: supermercado compra"
                    />
                  </label>
                  <div className="settings-drawer-double">
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe mínimo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={quickCategoryForm.initialRule.minAmount}
                        onChange={event =>
                          setQuickCategoryForm(prev => ({
                            ...prev,
                            initialRule: { ...prev.initialRule, minAmount: event.target.value },
                          }))
                        }
                        placeholder="Opcional"
                      />
                    </label>
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe máximo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={quickCategoryForm.initialRule.maxAmount}
                        onChange={event =>
                          setQuickCategoryForm(prev => ({
                            ...prev,
                            initialRule: { ...prev.initialRule, maxAmount: event.target.value },
                          }))
                        }
                        placeholder="Opcional"
                      />
                    </label>
                  </div>
                </div>

                <div className="settings-drawer-actions">
                  <button type="submit" className="btn btn-primary" disabled={quickCategorySubmitting}>
                    {quickCategorySubmitting ? "Guardando..." : "Crear categoría"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={closeQuickCategoryModal} disabled={quickCategorySubmitting}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
      )}
      <PlanLimitModal
        open={Boolean(planLimitDetails)}
        details={planLimitDetails}
        onClose={() => setPlanLimitDetails(null)}
        onUpgradeMonthly={handleUpgradeMonthly}
        onUpgradeYearly={handleUpgradeYearly}
        loading={billingActionLoading === "month" || billingActionLoading === "year"}
      />
    </DashboardLayout>
  );
}
